import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCatalog } from "../catalogStore";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

/* =========================================================
   Every tile comes from GET /api/categories (active only,
   already sorted by the admin's sort order). There is no
   hard-coded category list here — add a category and upload
   its picture in the Admin Dashboard and it appears here.

   Desktop / tablet : round picture tiles (unchanged)
   Mobile (≤ 640px) : tall picture cards in 2 columns with a
                      grey label box underneath — name + "N Items"
   ========================================================= */

const getImgUrl = (img) => {
  if (!img) return null;
  if (img.startsWith("http")) return img;       // full URL (Cloudinary)
  if (img.startsWith("/")) return img;          // file in your React /public folder
  if (img.startsWith("uploads/")) return `${API}/${img}`;
  return `${API}/uploads/${img}`;
};

const initials = (name) =>
  String(name || "")
    .split(/[\s–-]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function ShopByCategory() {
  const navigate = useNavigate();

  // Categories (with live product counts) come from the shared storefront copy —
  // already in memory / this browser, so the tiles appear instantly.
  const { data, error } = useCatalog();
  const categories = data ? data.categories : [];
  const loading    = !data && !error;
  const failed     = !data && error;
  const [brokenImages, setBrokenImages] = useState({});

  const goToCategory = (category) => navigate(`/category/${category.slug}`);

  const handleKeyDown = (event, category) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToCategory(category);
    }
  };

  return (
    <section style={styles.section} data-section="shop-by-category">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        [data-section="shop-by-category"] {
          position: relative !important;
          z-index: 1 !important;
          overflow-x: hidden !important;
          overflow-y: visible !important;
          height: auto !important;
          max-height: none !important;
          clear: both !important;
        }

        /* ───────── desktop / tablet: round tiles ───────── */
        .cat-tile {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          cursor: pointer;
        }
        .cat-tile:hover { transform: translateY(-6px); }
        .cat-tile:hover .cat-circle {
          box-shadow: 0 12px 30px rgba(200, 160, 74, 0.35);
          border-color: #c8a04a;
        }
        .cat-circle { transition: box-shadow 0.25s ease, border-color 0.25s ease; }
        .cat-circle-image {
          width: 100%;
          height: 100%;
          display: block;
          border-radius: 50%;
          object-fit: cover;
        }
        .cat-tile:focus-visible {
          outline: 2px solid #c8a04a;
          outline-offset: 6px;
          border-radius: 10px;
        }

        .cat-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        .cat-count-mobile { display: none; }

        @media (max-width: 900px) {
          .cat-grid {
            grid-template-columns: repeat(3, minmax(100px, 1fr)) !important;
            gap: 24px 20px !important;
          }
        }

        /* ───────── mobile: rounded picture tiles, 3 per row ─────────
           (soft offset edge + name under the picture). The edge colour
            comes from the Mana Vastralu logo — change it here: */
        [data-section="shop-by-category"] { --cat-edge: #f0a79a; } /* coral-peach from the logo's flowers */

        @media (max-width: 640px) {
          [data-section="shop-by-category"] { padding: 32px 0 40px !important; }
          .cat-header { padding: 0 16px; }

          .cat-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 22px 14px !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 16px 0 14px;
            box-sizing: border-box;
            justify-items: stretch !important;
          }

          .cat-tile {
            max-width: none !important;
            width: 100% !important;
            align-items: stretch !important;
            background: transparent;
          }
          .cat-tile:hover { transform: none; }

          /* the picture: rounded, uneven corners + soft offset edge */
          .cat-circle {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1 / 1.05;
            border-radius: 10px 34px 10px 28px !important;
            border: none !important;
            box-shadow: 5px 5px 0 var(--cat-edge) !important;
            margin: 0 0 12px !important;
            background: #f4f1f2 !important;
          }
          .cat-circle-image {
            border-radius: 0;
            object-position: center top;
          }
          .cat-fallback { font-size: 34px !important; }

          /* name under the picture — centred, no box */
          .cat-info {
            align-items: center;
            background: transparent;
            border-top: none;
            padding: 0 2px;
            min-height: 0;
            text-align: center;
          }
          .cat-name {
            font-size: 13px !important;
            font-weight: 500 !important;
            color: #2a2a2a !important;
            text-transform: none;
            text-align: center !important;
            line-height: 1.25 !important;
            letter-spacing: 0;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .cat-count {
            font-size: 11px !important;
            color: #8a8a8a !important;
            margin-top: 3px !important;
          }
          .cat-count-desktop { display: none; }
          .cat-count-mobile { display: inline; }
        }

        /* very small phones: 2 per row so the pictures stay big enough */
        @media (max-width: 360px) {
          .cat-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <div className="cat-header" style={styles.headerRow}>
        <h2 style={styles.heading}>Shop by Category</h2>
        <p style={styles.subheading}>Explore our curated collections</p>
      </div>

      {loading && <p style={styles.message}>Loading categories…</p>}
      {!loading && failed && <p style={styles.message}>Categories are unavailable right now.</p>}
      {!loading && !failed && categories.length === 0 && (
        <p style={styles.message}>No categories available yet.</p>
      )}

      {categories.length > 0 && (
        <div className="cat-grid" style={styles.grid}>
          {categories.map((category) => {
            const image = getImgUrl(category.image);
            const imageIsAvailable = Boolean(image) && !brokenImages[category._id];
            const count = Number(category.productCount) || 0;

            return (
              <div
                key={category._id}
                className="cat-tile"
                style={styles.tile}
                onClick={() => goToCategory(category)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => handleKeyDown(event, category)}
              >
                <div className="cat-circle" style={{ ...styles.circle, backgroundColor: "#fdf6ee" }}>
                  {imageIsAvailable ? (
                    <img
                      className="cat-circle-image"
                      src={image}
                      alt={`${category.name} collection`}
                      loading="lazy"
                      onError={() =>
                        setBrokenImages((previous) => ({ ...previous, [category._id]: true }))
                      }
                    />
                  ) : (
                    <span className="cat-fallback" style={styles.circleFallback} aria-hidden="true">
                      {initials(category.name)}
                    </span>
                  )}
                </div>

                <div className="cat-info">
                  <div className="cat-name" style={styles.tileName}>{category.name}</div>
                  <div className="cat-count" style={styles.tileCount}>
                    <span className="cat-count-desktop">
                      {count} Product{count !== 1 ? "s" : ""}
                    </span>
                    <span className="cat-count-mobile">
                      {count} Item{count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* =========================================================
   STYLES  (desktop look unchanged)
   ========================================================= */
const styles = {
  section: {
    position: "relative",
    zIndex: 1,
    overflowX: "hidden",
    overflowY: "visible",
    height: "auto",
    maxHeight: "none",
    width: "100%",
    display: "block",
    padding: "48px 24px 56px",
    background: "#fffaf4",
    fontFamily: "'DM Sans', sans-serif",
  },
  headerRow: { textAlign: "center", marginBottom: 32 },
  heading: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 28,
    fontWeight: 600,
    color: "#3d1a0e",
    margin: "0 0 6px",
  },
  subheading: {
    fontSize: 13,
    color: "#9a7050",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    margin: 0,
  },
  message: { textAlign: "center", color: "#9a7050", fontSize: 14, margin: "24px 0" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(110px, 1fr))",
    gap: "32px 24px",
    maxWidth: 900,
    margin: "0 auto",
    justifyItems: "center",
    alignItems: "start",
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: 150,
  },
  circle: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    border: "2px solid rgba(200, 160, 74, 0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginBottom: 10,
    boxShadow: "0 4px 14px rgba(61, 26, 14, 0.08)",
  },
  circleFallback: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#c8a04a",
  },
  tileName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#3d1a0e",
    textAlign: "center",
    lineHeight: 1.3,
  },
  tileCount: { fontSize: 11, color: "#9a7050", marginTop: 2 },
};